import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, CardContent, Badge } from "@learn-me/ui";
import { adminApi } from "@/features/admin/api/adminApi";
import { formatCurrency, formatDate } from "@learn-me/utils";
import { DollarSign, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";

export default function PaymentsPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>("");
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-payments", page, status],
    queryFn: () => adminApi.getPayments(page, 20, status || undefined),
  });

  const refundMutation = useMutation({
    mutationFn: (paymentId: string) => adminApi.refundPayment(paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-payments"] });
      toast.success("Pago reembolsado");
    },
    onError: () => {
      toast.error("Error al procesar el reembolso");
    },
  });

  const handleRefund = (paymentId: string, amount: number) => {
    if (
      confirm(
        `Estas seguro de reembolsar ${formatCurrency(amount, "USD")}? Esta accion no se puede deshacer.`,
      )
    ) {
      refundMutation.mutate(paymentId);
    }
  };

  const getStatusBadge = (paymentStatus: string) => {
    switch (paymentStatus) {
      case "COMPLETED":
        return <Badge variant="success">Completado</Badge>;
      case "PENDING":
        return <Badge variant="warning">Pendiente</Badge>;
      case "FAILED":
        return <Badge variant="destructive">Fallido</Badge>;
      case "REFUNDED":
        return <Badge variant="outline">Reembolsado</Badge>;
      default:
        return <Badge variant="outline">{paymentStatus}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Pagos</h1>
        <p className="text-muted-foreground">
          Historial de transacciones de la plataforma.
        </p>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <select
          className="px-3 py-2 border rounded-lg bg-background"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos los estados</option>
          <option value="COMPLETED">Completados</option>
          <option value="PENDING">Pendientes</option>
          <option value="FAILED">Fallidos</option>
          <option value="REFUNDED">Reembolsados</option>
        </select>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4 flex gap-4">
                <div className="w-12 h-12 bg-muted rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 bg-muted rounded w-1/4" />
                  <div className="h-4 bg-muted rounded w-1/3" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !data?.items?.length ? (
        <Card>
          <CardContent className="py-16 text-center">
            <DollarSign className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No hay pagos</h2>
            <p className="text-muted-foreground">
              {status
                ? "No hay pagos con ese estado."
                : "Aun no se han registrado pagos."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {data.items.map((payment) => (
              <Card key={payment.id}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <DollarSign className="h-6 w-6 text-green-500" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold">{payment.user.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {payment.user.email}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                      {payment.course.title}
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="text-xl font-bold">
                      {formatCurrency(payment.amount, payment.currency)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {payment.paidAt
                        ? formatDate(new Date(payment.paidAt))
                        : formatDate(new Date(payment.createdAt))}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {getStatusBadge(payment.status)}

                    {payment.status === "COMPLETED" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRefund(payment.id, payment.amount)}
                        disabled={refundMutation.isPending}
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {data.pagination.totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Anterior
              </Button>
              <span className="flex items-center px-4">
                Pagina {page} de {data.pagination.totalPages}
              </span>
              <Button
                variant="outline"
                disabled={page === data.pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Siguiente
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
